<script setup>
import { computed, onMounted, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import ShiftManagementAPI from 'dashboard/api/shiftManagement';
import Button from 'dashboard/components-next/button/Button.vue';
import Switch from 'dashboard/components-next/switch/Switch.vue';

const { t } = useI18n();

const loading = ref(true);
const saving = ref(false);
const dashboard = ref({
  users: [],
  templates: [],
  inboxes: [],
  eligible_user_ids: [],
  manageable_user_ids: [],
  handover_recipient_ids: [],
  permissions: {},
  recent_handovers: [],
  recent_schedule_events: [],
});
const selectedDate = ref(new Date().toISOString().slice(0, 10));
const scheduleDrafts = ref({});
const selectedScheduleUserIds = ref([]);
const dirtyScheduleUserIds = ref([]);
const bulkTemplateId = ref('');
const bulkLocked = ref(false);
const selfTemplateId = ref('');
const templateDrafts = ref({});
const handoverTargets = ref({});
const selectedInboxIds = ref([]);
const newTemplate = ref({ name: '', start_time: '08:00', end_time: '17:00' });

const users = computed(() => dashboard.value.users || []);
const inboxes = computed(() => dashboard.value.inboxes || []);
const templates = computed(() => dashboard.value.templates || []);
const isManager = computed(() => Boolean(dashboard.value.manager));
const canManageTemplates = computed(() =>
  Boolean(dashboard.value.permissions?.manage_templates)
);
const canManageSupervisors = computed(() =>
  Boolean(dashboard.value.permissions?.manage_supervisors)
);
const isToday = computed(() => selectedDate.value === dashboard.value.today);
const currentUser = computed(() =>
  users.value.find(user => user.id === dashboard.value.current_user_id)
);
const currentUserInboxes = computed(() =>
  inboxes.value.filter(
    inbox => inbox.owner?.id === dashboard.value.current_user_id
  )
);
const selfCanStart = computed(() => {
  const schedule = currentUser.value?.schedule;
  return Boolean(
    isToday.value &&
      schedule?.presence !== 'present' &&
      (schedule?.locked || selfTemplateId.value)
  );
});
const selectedInboxes = computed(() =>
  inboxes.value.filter(inbox => selectedInboxIds.value.includes(inbox.id))
);
const eligibleUsers = computed(() => {
  const eligibleIds = new Set(dashboard.value.eligible_user_ids || []);
  return users.value.filter(user => eligibleIds.has(user.id));
});
const manageableUsers = computed(() => {
  const manageableIds = new Set(dashboard.value.manageable_user_ids || []);
  return users.value.filter(user => manageableIds.has(user.id));
});
const handoverUsers = computed(() => {
  const recipientIds = new Set(dashboard.value.handover_recipient_ids || []);
  return users.value.filter(user => recipientIds.has(user.id));
});
const activeUsers = computed(() => {
  const visibleUsers = isManager.value
    ? manageableUsers.value
    : [currentUser.value].filter(Boolean);
  return visibleUsers.filter(user => user.schedule?.active_now);
});
const distributionCandidates = computed(() => {
  const presentAgents = handoverUsers.value.filter(
    user => user.role !== 'administrator' && user.schedule?.active_now
  );
  return presentAgents.length ? presentAgents : handoverUsers.value;
});

const hydrateDrafts = () => {
  scheduleDrafts.value = Object.fromEntries(
    users.value.map(user => [
      user.id,
      {
        template_id: user.schedule
          ? user.schedule.template_id?.toString() || 'custom'
          : '',
        start_time: user.schedule?.start_time || '',
        end_time: user.schedule?.end_time || '',
        supervisor: Boolean(user.schedule?.supervisor),
        locked: Boolean(user.schedule?.locked),
      },
    ])
  );
  templateDrafts.value = Object.fromEntries(
    templates.value.map(template => [template.id, { ...template }])
  );
  handoverTargets.value = Object.fromEntries(
    inboxes.value.map(inbox => [inbox.id, ''])
  );
  selectedScheduleUserIds.value = [];
  dirtyScheduleUserIds.value = [];
  bulkTemplateId.value = '';
  selfTemplateId.value =
    currentUser.value?.schedule?.template_id?.toString() ||
    dashboard.value.recommended_template_id?.toString() ||
    '';
};

const loadDashboard = async () => {
  loading.value = true;
  try {
    const { data } = await ShiftManagementAPI.getDashboard(selectedDate.value);
    dashboard.value = data;
    selectedInboxIds.value = [];
    hydrateDrafts();
  } catch (error) {
    useAlert(error.response?.data?.error || t('SHIFT_MANAGEMENT.ERRORS.LOAD'));
  } finally {
    loading.value = false;
  }
};

const changeDate = days => {
  const date = new Date(`${selectedDate.value}T12:00:00`);
  date.setDate(date.getDate() + days);
  selectedDate.value = date.toISOString().slice(0, 10);
};

const selectTemplate = userId => {
  const draft = scheduleDrafts.value[userId];
  markScheduleDirty(userId);
  if (!draft.template_id) {
    Object.assign(draft, { start_time: '', end_time: '' });
    return;
  }
  if (draft.template_id === 'custom') {
    Object.assign(draft, {
      start_time: draft.start_time || '08:00',
      end_time: draft.end_time || '17:00',
    });
    return;
  }
  const template = templates.value.find(
    item => item.id.toString() === draft.template_id
  );
  if (!template) return;
  Object.assign(draft, {
    start_time: template.start_time,
    end_time: template.end_time,
  });
};

const markScheduleDirty = userId => {
  if (!dirtyScheduleUserIds.value.includes(userId)) {
    dirtyScheduleUserIds.value = [...dirtyScheduleUserIds.value, userId];
  }
};

const schedulePayload = userId => {
  const draft = scheduleDrafts.value[userId];
  return {
    template_id: draft.template_id === 'custom' ? null : draft.template_id,
    start_time: draft.start_time,
    end_time: draft.end_time,
    supervisor: draft.supervisor,
    locked: draft.locked,
  };
};

const saveSchedule = async user => {
  saving.value = true;
  try {
    await ShiftManagementAPI.saveSchedule(
      user.id,
      selectedDate.value,
      schedulePayload(user.id)
    );
    useAlert(t('SHIFT_MANAGEMENT.ALERTS.SCHEDULE_SAVED'));
    await loadDashboard();
  } catch (error) {
    useAlert(error.response?.data?.error || t('SHIFT_MANAGEMENT.ERRORS.SAVE'));
  } finally {
    saving.value = false;
  }
};

const toggleScheduleUser = userId => {
  selectedScheduleUserIds.value = selectedScheduleUserIds.value.includes(userId)
    ? selectedScheduleUserIds.value.filter(id => id !== userId)
    : [...selectedScheduleUserIds.value, userId];
};

const toggleAllScheduleUsers = () => {
  selectedScheduleUserIds.value =
    selectedScheduleUserIds.value.length === manageableUsers.value.length
      ? []
      : manageableUsers.value.map(user => user.id);
};

const selectAllUnscheduled = () => {
  selectedScheduleUserIds.value = manageableUsers.value
    .filter(user => !user.schedule)
    .map(user => user.id);
};

const applyBulkTemplate = () => {
  const template = templates.value.find(
    item => item.id.toString() === bulkTemplateId.value
  );
  if (!template || !selectedScheduleUserIds.value.length) {
    useAlert(t('SHIFT_MANAGEMENT.ERRORS.CHOOSE_BULK_SHIFT'));
    return;
  }
  selectedScheduleUserIds.value.forEach(userId => {
    Object.assign(scheduleDrafts.value[userId], {
      template_id: template.id.toString(),
      start_time: template.start_time,
      end_time: template.end_time,
      locked: bulkLocked.value,
    });
    markScheduleDirty(userId);
  });
};

const saveSchedules = async userIds => {
  const validUserIds = userIds.filter(userId => {
    const user = users.value.find(item => item.id === userId);
    return user && canSaveSchedule(user);
  });
  if (!validUserIds.length) {
    useAlert(t('SHIFT_MANAGEMENT.ERRORS.NO_SCHEDULE_CHANGES'));
    return;
  }

  saving.value = true;
  try {
    await ShiftManagementAPI.saveSchedules(
      selectedDate.value,
      validUserIds.map(userId => ({
        user_id: userId,
        schedule: schedulePayload(userId),
      }))
    );
    useAlert(
      t('SHIFT_MANAGEMENT.ALERTS.SCHEDULES_SAVED', {
        count: validUserIds.length,
      })
    );
    await loadDashboard();
  } catch (error) {
    useAlert(error.response?.data?.error || t('SHIFT_MANAGEMENT.ERRORS.SAVE'));
  } finally {
    saving.value = false;
  }
};

const startSelfSchedule = async () => {
  if (!selfCanStart.value) return;
  saving.value = true;
  try {
    await ShiftManagementAPI.startSelfSchedule(
      selectedDate.value,
      Number(selfTemplateId.value) || null
    );
    useAlert(t('SHIFT_MANAGEMENT.ALERTS.SHIFT_STARTED'));
    await loadDashboard();
  } catch (error) {
    useAlert(
      error.response?.data?.error || t('SHIFT_MANAGEMENT.ERRORS.START_SHIFT')
    );
  } finally {
    saving.value = false;
  }
};

const setPresence = async (user, present) => {
  try {
    await ShiftManagementAPI.setPresence(
      user.id,
      selectedDate.value,
      present ? 'present' : 'absent'
    );
    await loadDashboard();
  } catch (error) {
    useAlert(
      error.response?.data?.error || t('SHIFT_MANAGEMENT.ERRORS.PRESENCE')
    );
  }
};

const saveTemplate = async template => {
  saving.value = true;
  try {
    const draft = templateDrafts.value[template.id];
    await ShiftManagementAPI.updateTemplate(template.id, draft);
    useAlert(t('SHIFT_MANAGEMENT.ALERTS.TEMPLATE_SAVED'));
    await loadDashboard();
  } catch (error) {
    useAlert(error.response?.data?.error || t('SHIFT_MANAGEMENT.ERRORS.SAVE'));
  } finally {
    saving.value = false;
  }
};

const createTemplate = async () => {
  saving.value = true;
  try {
    await ShiftManagementAPI.createTemplate(newTemplate.value);
    newTemplate.value = { name: '', start_time: '08:00', end_time: '17:00' };
    useAlert(t('SHIFT_MANAGEMENT.ALERTS.TEMPLATE_CREATED'));
    await loadDashboard();
  } catch (error) {
    useAlert(error.response?.data?.error || t('SHIFT_MANAGEMENT.ERRORS.SAVE'));
  } finally {
    saving.value = false;
  }
};

const deleteTemplate = async template => {
  // eslint-disable-next-line no-alert
  const confirmed = window.confirm(
    t('SHIFT_MANAGEMENT.TEMPLATES.DELETE_CONFIRM', { name: template.name })
  );
  if (!confirmed) return;
  try {
    await ShiftManagementAPI.deleteTemplate(template.id);
    await loadDashboard();
  } catch (error) {
    useAlert(error.response?.data?.error || t('SHIFT_MANAGEMENT.ERRORS.SAVE'));
  }
};

const canHandover = inbox =>
  isManager.value || inbox.owner?.id === dashboard.value.current_user_id;

const handoverInbox = async inbox => {
  const toUserId = Number(handoverTargets.value[inbox.id]);
  if (!toUserId) {
    useAlert(t('SHIFT_MANAGEMENT.ERRORS.CHOOSE_RECIPIENT'));
    return;
  }

  saving.value = true;
  try {
    const { data } = await ShiftManagementAPI.handover(inbox.id, toUserId);
    useAlert(
      t('SHIFT_MANAGEMENT.ALERTS.HANDOVER_COMPLETE', {
        count: data.conversations_transferred,
      })
    );
    await loadDashboard();
  } catch (error) {
    useAlert(
      error.response?.data?.error || t('SHIFT_MANAGEMENT.ERRORS.HANDOVER')
    );
  } finally {
    saving.value = false;
  }
};

const toggleInbox = inboxId => {
  selectedInboxIds.value = selectedInboxIds.value.includes(inboxId)
    ? selectedInboxIds.value.filter(id => id !== inboxId)
    : [...selectedInboxIds.value, inboxId];
};

const toggleAllInboxes = () => {
  selectedInboxIds.value =
    selectedInboxIds.value.length === inboxes.value.length
      ? []
      : inboxes.value.map(inbox => inbox.id);
};

const distributeSelected = () => {
  if (!distributionCandidates.value.length) {
    useAlert(t('SHIFT_MANAGEMENT.ERRORS.NO_ELIGIBLE_AGENT'));
    return;
  }
  const workload = Object.fromEntries(
    distributionCandidates.value.map(user => [
      user.id,
      inboxes.value.filter(inbox => inbox.owner?.id === user.id).length,
    ])
  );

  selectedInboxes.value.forEach(inbox => {
    const target = [...distributionCandidates.value].sort(
      (left, right) => workload[left.id] - workload[right.id]
    )[0];
    handoverTargets.value[inbox.id] = target.id.toString();
    workload[target.id] += 1;
  });
};

const bulkHandover = async () => {
  const handovers = selectedInboxes.value.map(inbox => ({
    inbox_id: inbox.id,
    to_user_id: Number(handoverTargets.value[inbox.id]),
  }));
  if (!handovers.length || handovers.some(item => !item.to_user_id)) {
    useAlert(t('SHIFT_MANAGEMENT.ERRORS.CHOOSE_ALL_RECIPIENTS'));
    return;
  }

  saving.value = true;
  try {
    await ShiftManagementAPI.bulkHandover(handovers);
    useAlert(
      t('SHIFT_MANAGEMENT.ALERTS.BULK_COMPLETE', { count: handovers.length })
    );
    await loadDashboard();
  } catch (error) {
    useAlert(
      error.response?.data?.error || t('SHIFT_MANAGEMENT.ERRORS.HANDOVER')
    );
  } finally {
    saving.value = false;
  }
};

const userOptions = inbox =>
  handoverUsers.value.filter(user => user.id !== inbox.owner?.id);

const scheduleName = user => {
  if (!user.schedule) return t('SHIFT_MANAGEMENT.SCHEDULE.NO_SHIFT');
  return t('SHIFT_MANAGEMENT.SCHEDULE.TIME_RANGE', {
    start: user.schedule.start_time,
    end: user.schedule.end_time,
  });
};

const presenceLabel = user => {
  if (!user.schedule) return t('SHIFT_MANAGEMENT.PRESENCE.UNSCHEDULED');
  if (user.schedule.active_now) return t('SHIFT_MANAGEMENT.PRESENCE.ACTIVE');
  if (user.schedule.presence === 'present') {
    return t('SHIFT_MANAGEMENT.PRESENCE.PRESENT');
  }
  if (user.schedule.presence === 'absent') {
    return t('SHIFT_MANAGEMENT.PRESENCE.ABSENT');
  }
  return t('SHIFT_MANAGEMENT.PRESENCE.SCHEDULED');
};

const inboxIdLabel = inbox =>
  t('SHIFT_MANAGEMENT.HANDOVER.INBOX_ID', { id: inbox.id });

const ownerDutyLabel = inbox =>
  inbox.owner?.active_now
    ? t('SHIFT_MANAGEMENT.HANDOVER.ON_DUTY')
    : t('SHIFT_MANAGEMENT.HANDOVER.OFF_DUTY');

const canSaveSchedule = user => {
  const draft = scheduleDrafts.value[user.id];
  return Boolean(draft?.template_id && draft.start_time && draft.end_time);
};

const userOptionLabel = user =>
  t('SHIFT_MANAGEMENT.HANDOVER.USER_OPTION', {
    name: user.name,
    schedule: scheduleName(user),
  });

const historyDescription = item =>
  t('SHIFT_MANAGEMENT.HISTORY.TRANSFER', {
    from: item.from_name || t('SHIFT_MANAGEMENT.HANDOVER.UNASSIGNED'),
    to: item.to_name,
  });

const scheduleEventDescription = item => {
  const key = {
    manager_saved: 'MANAGER_SAVED',
    self_started: 'SELF_STARTED',
    presence_changed: 'PRESENCE_CHANGED',
  }[item.action];

  if (!key) return item.action;
  return t(`SHIFT_MANAGEMENT.SCHEDULE_HISTORY.${key}`, {
    actor: item.actor_name,
    user: item.user_name,
    shift: item.shift_name || t('SHIFT_MANAGEMENT.SCHEDULE.CUSTOM'),
  });
};

watch(selectedDate, loadDashboard);
onMounted(loadDashboard);
</script>

<template>
  <main class="flex-1 min-w-0 h-full overflow-y-auto bg-n-background">
    <header
      class="sticky top-0 z-10 bg-n-background border-b border-n-weak px-5 py-4"
    >
      <div
        class="flex flex-wrap items-center justify-between gap-3 max-w-[1440px] mx-auto"
      >
        <div>
          <h1 class="text-xl font-semibold text-n-slate-12">
            {{ t('SHIFT_MANAGEMENT.TITLE') }}
          </h1>
          <p class="text-sm text-n-slate-10 mt-1">
            {{ t('SHIFT_MANAGEMENT.SUBTITLE') }}
          </p>
        </div>
        <div class="flex items-center gap-2">
          <Button
            faded
            slate
            sm
            icon="i-lucide-chevron-left"
            :label="t('SHIFT_MANAGEMENT.DATE.PREVIOUS')"
            @click="changeDate(-1)"
          />
          <input
            v-model="selectedDate"
            type="date"
            class="h-9 px-3 rounded-md border border-n-weak bg-n-alpha-2 text-sm text-n-slate-12"
          />
          <Button
            faded
            slate
            sm
            trailing-icon
            icon="i-lucide-chevron-right"
            :label="t('SHIFT_MANAGEMENT.DATE.NEXT')"
            @click="changeDate(1)"
          />
        </div>
      </div>
    </header>

    <div
      v-if="loading"
      class="h-64 flex items-center justify-center text-n-slate-10"
    >
      {{ t('SHIFT_MANAGEMENT.LOADING') }}
    </div>

    <div v-else class="max-w-[1440px] mx-auto">
      <section class="grid grid-cols-2 lg:grid-cols-4 border-b border-n-weak">
        <div class="px-5 py-4 border-r border-n-weak">
          <div class="text-xs text-n-slate-9">
            {{ t('SHIFT_MANAGEMENT.SUMMARY.AGENTS') }}
          </div>
          <div class="text-2xl font-semibold text-n-slate-12 mt-1">
            {{ isManager ? manageableUsers.length : currentUser ? 1 : 0 }}
          </div>
        </div>
        <div class="px-5 py-4 lg:border-r border-n-weak">
          <div class="text-xs text-n-slate-9">
            {{ t('SHIFT_MANAGEMENT.SUMMARY.PRESENT') }}
          </div>
          <div class="text-2xl font-semibold text-n-teal-11 mt-1">
            {{ activeUsers.length }}
          </div>
        </div>
        <div class="px-5 py-4 border-r border-t lg:border-t-0 border-n-weak">
          <div class="text-xs text-n-slate-9">
            {{ t('SHIFT_MANAGEMENT.SUMMARY.ZALO') }}
          </div>
          <div class="text-2xl font-semibold text-n-slate-12 mt-1">
            {{ inboxes.length }}
          </div>
        </div>
        <div class="px-5 py-4 border-t lg:border-t-0 border-n-weak">
          <div class="text-xs text-n-slate-9">
            {{ t('SHIFT_MANAGEMENT.SUMMARY.UNASSIGNED') }}
          </div>
          <div class="text-2xl font-semibold text-n-ruby-11 mt-1">
            {{ inboxes.filter(inbox => !inbox.owner).length }}
          </div>
        </div>
      </section>

      <section
        v-if="!isManager && isToday && currentUser"
        class="border-b border-n-weak px-5 py-5"
      >
        <div class="flex flex-wrap items-center justify-between gap-4">
          <div class="min-w-0">
            <div class="flex items-center gap-2">
              <span class="i-lucide-clock-3 size-5 text-n-brand" />
              <h2 class="font-semibold text-n-slate-12">
                {{ t('SHIFT_MANAGEMENT.SELF_SERVICE.TITLE') }}
              </h2>
            </div>
            <p class="text-sm text-n-slate-10 mt-1">
              <template v-if="currentUserInboxes.length">
                {{
                  t('SHIFT_MANAGEMENT.SELF_SERVICE.OWNED_ZALO', {
                    names: currentUserInboxes
                      .map(inbox => inbox.name)
                      .join(', '),
                  })
                }}
              </template>
              <template v-else>
                {{ t('SHIFT_MANAGEMENT.SELF_SERVICE.NO_ZALO') }}
              </template>
            </p>
          </div>
          <div
            v-if="currentUser.schedule?.presence === 'present'"
            class="flex items-center gap-2 text-sm font-medium text-n-teal-11"
          >
            <span class="i-lucide-circle-check size-5" />
            {{ t('SHIFT_MANAGEMENT.SELF_SERVICE.STARTED') }} ·
            {{ scheduleName(currentUser) }}
          </div>
          <div v-else class="flex flex-wrap items-center gap-2">
            <span
              v-if="currentUser.schedule?.locked"
              class="text-sm font-medium text-n-slate-12"
            >
              {{ scheduleName(currentUser) }} ·
              {{ t('SHIFT_MANAGEMENT.SELF_SERVICE.LOCKED') }}
            </span>
            <select
              v-else
              v-model="selfTemplateId"
              class="h-9 min-w-56 px-2 rounded-md border border-n-weak bg-n-alpha-2 text-sm"
            >
              <option value="" disabled>
                {{ t('SHIFT_MANAGEMENT.SELF_SERVICE.CHOOSE') }}
              </option>
              <option
                v-for="template in templates"
                :key="template.id"
                :value="template.id.toString()"
              >
                {{ template.name }} · {{ template.start_time }}–{{
                  template.end_time
                }}
                <template
                  v-if="template.id === dashboard.recommended_template_id"
                >
                  · {{ t('SHIFT_MANAGEMENT.SELF_SERVICE.RECOMMENDED') }}
                </template>
              </option>
            </select>
            <Button
              sm
              icon="i-lucide-play"
              :label="t('SHIFT_MANAGEMENT.SELF_SERVICE.START')"
              :disabled="!selfCanStart"
              :is-loading="saving"
              @click="startSelfSchedule"
            />
          </div>
        </div>
      </section>

      <section
        v-if="canManageTemplates"
        class="border-b border-n-weak px-5 py-5"
      >
        <div class="flex items-center justify-between mb-4">
          <h2 class="font-semibold text-n-slate-12">
            {{ t('SHIFT_MANAGEMENT.TEMPLATES.TITLE') }}
          </h2>
        </div>
        <div class="grid gap-2 xl:grid-cols-2">
          <div
            v-for="template in templates"
            :key="template.id"
            class="grid grid-cols-[minmax(120px,1fr)_110px_110px_auto] gap-2 items-center"
          >
            <input
              v-model="templateDrafts[template.id].name"
              class="h-9 px-3 rounded-md border border-n-weak bg-n-alpha-2 text-sm"
            />
            <input
              v-model="templateDrafts[template.id].start_time"
              type="time"
              class="h-9 px-2 rounded-md border border-n-weak bg-n-alpha-2 text-sm"
            />
            <input
              v-model="templateDrafts[template.id].end_time"
              type="time"
              class="h-9 px-2 rounded-md border border-n-weak bg-n-alpha-2 text-sm"
            />
            <div class="flex gap-1">
              <Button
                faded
                slate
                sm
                icon="i-lucide-save"
                :label="t('SHIFT_MANAGEMENT.ACTIONS.SAVE')"
                @click="saveTemplate(template)"
              />
              <Button
                ghost
                ruby
                sm
                icon="i-lucide-trash-2"
                :label="t('SHIFT_MANAGEMENT.ACTIONS.DELETE')"
                @click="deleteTemplate(template)"
              />
            </div>
          </div>
          <div
            class="grid grid-cols-[minmax(120px,1fr)_110px_110px_auto] gap-2 items-center"
          >
            <input
              v-model="newTemplate.name"
              :placeholder="t('SHIFT_MANAGEMENT.TEMPLATES.NAME')"
              class="h-9 px-3 rounded-md border border-n-weak bg-n-alpha-2 text-sm"
            />
            <input
              v-model="newTemplate.start_time"
              type="time"
              class="h-9 px-2 rounded-md border border-n-weak bg-n-alpha-2 text-sm"
            />
            <input
              v-model="newTemplate.end_time"
              type="time"
              class="h-9 px-2 rounded-md border border-n-weak bg-n-alpha-2 text-sm"
            />
            <Button
              sm
              icon="i-lucide-plus"
              :label="t('SHIFT_MANAGEMENT.TEMPLATES.ADD')"
              :disabled="!newTemplate.name"
              @click="createTemplate"
            />
          </div>
        </div>
      </section>

      <section v-if="isManager" class="border-b border-n-weak">
        <div class="px-5 py-4">
          <div class="flex items-center justify-between">
            <div>
              <h2 class="font-semibold text-n-slate-12">
                {{ t('SHIFT_MANAGEMENT.SCHEDULE.TITLE') }}
              </h2>
              <span class="text-xs text-n-slate-9">
                {{
                  t('SHIFT_MANAGEMENT.SCHEDULE.UNSAVED', {
                    count: dirtyScheduleUserIds.length,
                  })
                }}
              </span>
            </div>
            <span class="text-xs text-n-slate-9">{{ selectedDate }}</span>
          </div>
          <div v-if="isManager" class="flex flex-wrap items-center gap-2 mt-3">
            <Button
              faded
              slate
              sm
              icon="i-lucide-list-checks"
              :label="t('SHIFT_MANAGEMENT.SCHEDULE.SELECT_UNSCHEDULED')"
              @click="selectAllUnscheduled"
            />
            <select
              v-model="bulkTemplateId"
              class="h-9 min-w-48 px-2 rounded-md border border-n-weak bg-n-alpha-2 text-sm"
            >
              <option value="" disabled>
                {{ t('SHIFT_MANAGEMENT.SCHEDULE.CHOOSE_BULK_SHIFT') }}
              </option>
              <option
                v-for="template in templates"
                :key="template.id"
                :value="template.id.toString()"
              >
                {{ template.name }} · {{ template.start_time }}–{{
                  template.end_time
                }}
              </option>
            </select>
            <label class="flex items-center gap-2 text-sm text-n-slate-11">
              <Switch v-model="bulkLocked" />
              {{ t('SHIFT_MANAGEMENT.SCHEDULE.LOCK_AFTER_APPLY') }}
            </label>
            <Button
              faded
              slate
              sm
              icon="i-lucide-wand-sparkles"
              :label="t('SHIFT_MANAGEMENT.SCHEDULE.APPLY_SELECTED')"
              :disabled="!selectedScheduleUserIds.length || !bulkTemplateId"
              @click="applyBulkTemplate"
            />
            <Button
              faded
              blue
              sm
              icon="i-lucide-save"
              :label="
                t('SHIFT_MANAGEMENT.SCHEDULE.SAVE_SELECTED', {
                  count: selectedScheduleUserIds.length,
                })
              "
              :disabled="!selectedScheduleUserIds.length"
              :is-loading="saving"
              @click="saveSchedules(selectedScheduleUserIds)"
            />
            <Button
              sm
              icon="i-lucide-save-all"
              :label="
                t('SHIFT_MANAGEMENT.SCHEDULE.SAVE_ALL', {
                  count: dirtyScheduleUserIds.length,
                })
              "
              :disabled="!dirtyScheduleUserIds.length"
              :is-loading="saving"
              @click="saveSchedules(dirtyScheduleUserIds)"
            />
          </div>
        </div>
        <div class="overflow-x-auto">
          <table class="w-full min-w-[1080px] text-sm">
            <thead class="bg-n-alpha-2 text-xs text-n-slate-9">
              <tr>
                <th v-if="isManager" class="w-10 px-5 py-3">
                  <input
                    type="checkbox"
                    class="size-4 accent-n-brand"
                    :checked="
                      Boolean(manageableUsers.length) &&
                      selectedScheduleUserIds.length === manageableUsers.length
                    "
                    :aria-label="t('SHIFT_MANAGEMENT.SCHEDULE.SELECT_ALL')"
                    @change="toggleAllScheduleUsers"
                  />
                </th>
                <th class="text-left font-medium px-5 py-3">
                  {{ t('SHIFT_MANAGEMENT.SCHEDULE.AGENT') }}
                </th>
                <th class="text-left font-medium px-3 py-3">
                  {{ t('SHIFT_MANAGEMENT.SCHEDULE.SHIFT') }}
                </th>
                <th class="text-left font-medium px-3 py-3">
                  {{ t('SHIFT_MANAGEMENT.SCHEDULE.TIME') }}
                </th>
                <th class="text-left font-medium px-3 py-3">
                  {{ t('SHIFT_MANAGEMENT.SCHEDULE.SUPERVISOR') }}
                </th>
                <th v-if="isManager" class="text-left font-medium px-3 py-3">
                  {{ t('SHIFT_MANAGEMENT.SCHEDULE.LOCKED') }}
                </th>
                <th class="text-left font-medium px-3 py-3">
                  {{ t('SHIFT_MANAGEMENT.SCHEDULE.PRESENCE') }}
                </th>
                <th class="text-right font-medium px-5 py-3">
                  {{ t('SHIFT_MANAGEMENT.SCHEDULE.ACTION') }}
                </th>
              </tr>
            </thead>
            <tbody class="divide-y divide-n-weak">
              <tr
                v-for="user in manageableUsers"
                :key="user.id"
                class="hover:bg-n-alpha-1"
              >
                <td v-if="isManager" class="px-5 py-3">
                  <input
                    type="checkbox"
                    class="size-4 accent-n-brand"
                    :checked="selectedScheduleUserIds.includes(user.id)"
                    @change="toggleScheduleUser(user.id)"
                  />
                </td>
                <td class="px-5 py-3">
                  <div class="font-medium text-n-slate-12">{{ user.name }}</div>
                  <div class="text-xs text-n-slate-9">{{ user.email }}</div>
                </td>
                <td class="px-3 py-3">
                  <select
                    v-if="isManager"
                    v-model="scheduleDrafts[user.id].template_id"
                    class="h-9 min-w-36 px-2 rounded-md border border-n-weak bg-n-alpha-2"
                    @change="selectTemplate(user.id)"
                  >
                    <option value="" disabled>
                      {{ t('SHIFT_MANAGEMENT.SCHEDULE.NO_SHIFT') }}
                    </option>
                    <option value="custom">
                      {{ t('SHIFT_MANAGEMENT.SCHEDULE.CUSTOM') }}
                    </option>
                    <option
                      v-for="template in templates"
                      :key="template.id"
                      :value="template.id.toString()"
                    >
                      {{ template.name }}
                    </option>
                  </select>
                  <span v-else class="text-n-slate-11">{{
                    scheduleName(user)
                  }}</span>
                </td>
                <td class="px-3 py-3">
                  <div v-if="isManager" class="flex items-center gap-1">
                    <input
                      v-model="scheduleDrafts[user.id].start_time"
                      type="time"
                      class="h-9 w-28 px-2 rounded-md border border-n-weak bg-n-alpha-2"
                      @change="markScheduleDirty(user.id)"
                    />
                    <span class="text-n-slate-8">{{
                      t('SHIFT_MANAGEMENT.SCHEDULE.TIME_SEPARATOR')
                    }}</span>
                    <input
                      v-model="scheduleDrafts[user.id].end_time"
                      type="time"
                      class="h-9 w-28 px-2 rounded-md border border-n-weak bg-n-alpha-2"
                      @change="markScheduleDirty(user.id)"
                    />
                  </div>
                  <span v-else>{{ scheduleName(user) }}</span>
                </td>
                <td class="px-3 py-3">
                  <Switch
                    v-if="canManageSupervisors"
                    v-model="scheduleDrafts[user.id].supervisor"
                    @update:model-value="markScheduleDirty(user.id)"
                  />
                  <span v-else>{{
                    user.schedule?.supervisor
                      ? t('SHIFT_MANAGEMENT.COMMON.YES')
                      : t('SHIFT_MANAGEMENT.COMMON.NO')
                  }}</span>
                </td>
                <td v-if="isManager" class="px-3 py-3">
                  <div class="flex items-center gap-2">
                    <Switch
                      v-model="scheduleDrafts[user.id].locked"
                      @update:model-value="markScheduleDirty(user.id)"
                    />
                    <span class="text-xs text-n-slate-9">
                      {{
                        scheduleDrafts[user.id].locked
                          ? t('SHIFT_MANAGEMENT.SCHEDULE.LOCKED_YES')
                          : t('SHIFT_MANAGEMENT.SCHEDULE.LOCKED_NO')
                      }}
                    </span>
                  </div>
                </td>
                <td class="px-3 py-3">
                  <div class="flex items-center gap-2">
                    <Switch
                      :model-value="
                        Boolean(user.schedule?.presence === 'present')
                      "
                      :disabled="
                        !isToday ||
                        !user.schedule ||
                        (!isManager && user.id !== dashboard.current_user_id)
                      "
                      @update:model-value="value => setPresence(user, value)"
                    />
                    <span
                      class="text-xs font-medium"
                      :class="
                        user.schedule?.active_now
                          ? 'text-n-teal-11'
                          : 'text-n-slate-9'
                      "
                    >
                      {{ presenceLabel(user) }}
                    </span>
                  </div>
                </td>
                <td class="px-5 py-3 text-right">
                  <Button
                    v-if="isManager"
                    faded
                    slate
                    sm
                    icon="i-lucide-save"
                    :label="t('SHIFT_MANAGEMENT.ACTIONS.SAVE')"
                    :disabled="!canSaveSchedule(user)"
                    :is-loading="saving"
                    @click="saveSchedule(user)"
                  />
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </section>

      <section class="border-b border-n-weak">
        <div
          class="px-5 py-4 flex flex-wrap items-center justify-between gap-3"
        >
          <div>
            <h2 class="font-semibold text-n-slate-12">
              {{ t('SHIFT_MANAGEMENT.HANDOVER.TITLE') }}
            </h2>
            <div class="text-xs text-n-slate-9 mt-1">
              {{
                t('SHIFT_MANAGEMENT.HANDOVER.SELECTED', {
                  count: selectedInboxIds.length,
                })
              }}
            </div>
          </div>
          <div v-if="isManager" class="flex flex-wrap gap-2">
            <Button
              faded
              slate
              sm
              icon="i-lucide-list-checks"
              :label="t('SHIFT_MANAGEMENT.HANDOVER.SELECT_ALL')"
              @click="toggleAllInboxes"
            />
            <Button
              faded
              slate
              sm
              icon="i-lucide-scale"
              :label="t('SHIFT_MANAGEMENT.HANDOVER.DISTRIBUTE')"
              :disabled="!selectedInboxIds.length"
              @click="distributeSelected"
            />
            <Button
              sm
              icon="i-lucide-arrow-right-left"
              :label="
                t('SHIFT_MANAGEMENT.HANDOVER.BULK', {
                  count: selectedInboxIds.length,
                })
              "
              :disabled="!selectedInboxIds.length"
              :is-loading="saving"
              @click="bulkHandover"
            />
          </div>
        </div>
        <div class="overflow-x-auto">
          <table class="w-full min-w-[900px] text-sm">
            <thead class="bg-n-alpha-2 text-xs text-n-slate-9">
              <tr>
                <th v-if="isManager" class="w-10 px-5 py-3" />
                <th class="text-left font-medium px-3 py-3">
                  {{ t('SHIFT_MANAGEMENT.HANDOVER.ZALO') }}
                </th>
                <th class="text-left font-medium px-3 py-3">
                  {{ t('SHIFT_MANAGEMENT.HANDOVER.CURRENT') }}
                </th>
                <th class="text-left font-medium px-3 py-3">
                  {{ t('SHIFT_MANAGEMENT.HANDOVER.MEMBERS') }}
                </th>
                <th class="text-left font-medium px-3 py-3">
                  {{ t('SHIFT_MANAGEMENT.HANDOVER.CONVERSATIONS') }}
                </th>
                <th class="text-left font-medium px-3 py-3">
                  {{ t('SHIFT_MANAGEMENT.HANDOVER.RECIPIENT') }}
                </th>
                <th class="text-right font-medium px-5 py-3">
                  {{ t('SHIFT_MANAGEMENT.SCHEDULE.ACTION') }}
                </th>
              </tr>
            </thead>
            <tbody class="divide-y divide-n-weak">
              <tr
                v-for="inbox in inboxes"
                :key="inbox.id"
                class="hover:bg-n-alpha-1"
              >
                <td v-if="isManager" class="px-5 py-3">
                  <input
                    type="checkbox"
                    class="size-4 accent-n-brand"
                    :checked="selectedInboxIds.includes(inbox.id)"
                    @change="toggleInbox(inbox.id)"
                  />
                </td>
                <td class="px-3 py-3">
                  <div class="font-medium text-n-slate-12">
                    {{ inbox.name }}
                  </div>
                  <div class="text-xs text-n-slate-9">
                    {{ inboxIdLabel(inbox) }}
                  </div>
                </td>
                <td class="px-3 py-3">
                  <span
                    v-if="inbox.owner"
                    class="flex flex-col items-start gap-1"
                  >
                    <span class="font-medium text-n-slate-12">
                      {{ inbox.owner.name }}
                    </span>
                    <span
                      class="text-xs"
                      :class="
                        inbox.owner.active_now
                          ? 'text-n-teal-11'
                          : 'text-n-amber-11'
                      "
                    >
                      {{ ownerDutyLabel(inbox) }}
                    </span>
                  </span>
                  <span v-else class="text-n-ruby-11">{{
                    t('SHIFT_MANAGEMENT.HANDOVER.UNASSIGNED')
                  }}</span>
                </td>
                <td class="px-3 py-3 max-w-72">
                  <span v-if="inbox.members?.length" class="text-n-slate-10">
                    {{ inbox.members.map(member => member.name).join(', ') }}
                  </span>
                  <span v-else class="text-n-slate-8">
                    {{ t('SHIFT_MANAGEMENT.HANDOVER.NO_MEMBERS') }}
                  </span>
                </td>
                <td class="px-3 py-3 tabular-nums">
                  {{ inbox.active_conversations }}
                </td>
                <td class="px-3 py-3">
                  <select
                    v-model="handoverTargets[inbox.id]"
                    :disabled="!canHandover(inbox) || !isToday"
                    class="h-9 min-w-48 px-2 rounded-md border border-n-weak bg-n-alpha-2 disabled:opacity-50"
                  >
                    <option value="">
                      {{ t('SHIFT_MANAGEMENT.HANDOVER.CHOOSE') }}
                    </option>
                    <option
                      v-for="user in userOptions(inbox)"
                      :key="user.id"
                      :value="user.id.toString()"
                    >
                      {{ userOptionLabel(user) }}
                    </option>
                  </select>
                </td>
                <td class="px-5 py-3 text-right">
                  <Button
                    v-if="canHandover(inbox)"
                    faded
                    blue
                    sm
                    icon="i-lucide-arrow-right-left"
                    :label="
                      inbox.owner
                        ? t('SHIFT_MANAGEMENT.HANDOVER.ACTION')
                        : t('SHIFT_MANAGEMENT.HANDOVER.ASSIGN')
                    "
                    :disabled="!isToday || !handoverTargets[inbox.id]"
                    :is-loading="saving"
                    @click="handoverInbox(inbox)"
                  />
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </section>

      <section class="px-5 py-5">
        <h2 class="font-semibold text-n-slate-12 mb-3">
          {{ t('SHIFT_MANAGEMENT.HISTORY.TITLE') }}
        </h2>
        <div
          v-if="dashboard.recent_handovers.length"
          class="divide-y divide-n-weak border-y border-n-weak"
        >
          <div
            v-for="item in dashboard.recent_handovers"
            :key="item.id"
            class="py-3 grid gap-1 md:grid-cols-[1fr_1.5fr_auto] text-sm"
          >
            <span class="font-medium text-n-slate-12">{{
              item.inbox_name
            }}</span>
            <span class="text-n-slate-10">{{ historyDescription(item) }}</span>
            <span class="text-xs text-n-slate-9">
              {{
                t('SHIFT_MANAGEMENT.HISTORY.CONVERSATIONS', {
                  count: item.conversations_transferred,
                })
              }}
            </span>
          </div>
        </div>
        <div v-else class="text-sm text-n-slate-9 py-5">
          {{ t('SHIFT_MANAGEMENT.HISTORY.EMPTY') }}
        </div>
      </section>

      <section class="px-5 py-5 border-t border-n-weak">
        <h2 class="font-semibold text-n-slate-12 mb-3">
          {{ t('SHIFT_MANAGEMENT.SCHEDULE_HISTORY.TITLE') }}
        </h2>
        <div
          v-if="dashboard.recent_schedule_events.length"
          class="divide-y divide-n-weak border-y border-n-weak"
        >
          <div
            v-for="item in dashboard.recent_schedule_events"
            :key="item.id"
            class="py-3 grid gap-1 md:grid-cols-[1fr_auto] text-sm"
          >
            <span class="text-n-slate-11">
              {{ scheduleEventDescription(item) }}
            </span>
            <span class="text-xs text-n-slate-9">
              {{ item.work_date }}
            </span>
          </div>
        </div>
        <div v-else class="text-sm text-n-slate-9 py-5">
          {{ t('SHIFT_MANAGEMENT.SCHEDULE_HISTORY.EMPTY') }}
        </div>
      </section>
    </div>
  </main>
</template>
